import { useQuery } from '@tanstack/react-query';

import { QueryOptions } from '@cast/shared-types';
import { RebalancingConfigurationNode } from '@cast/types-cluster-autoscaler';
import { makeIsEnabled } from '@cast/utils';

import { apiClient } from '@cast/infrastructure-api';
import { useCurrentOrganization } from '@cast/infrastructure-organization';

const LIST_REBALANCING_NODES_QUERY_KEY = 'list-rebalancing-configuration-nodes';

type FetchAllParams = {
  organizationId: string;
  clusterId: string;
};

const fetchAllRebalancingNodes = async ({
  organizationId,
  clusterId,
}: FetchAllParams): Promise<RebalancingConfigurationNode[]> => {
  const allNodes: RebalancingConfigurationNode[] = [];
  let nextPageCursor: string | undefined;

  do {
    const { data } = await apiClient.clusterAutoscaler.listRebalancingNodes({
      organizationId,
      clusterId,
      searchRebalancingNodesRequest: {
        organizationId,
        clusterId,
        page: {
          limit: '1000',
          cursor: nextPageCursor,
        },
      },
    });

    allNodes.push(...(data.items ?? []));
    nextPageCursor = data.nextPageCursor;
  } while (nextPageCursor);

  return allNodes;
};

type QueryArgs = {
  params: Omit<FetchAllParams, 'organizationId'>;
  options?: QueryOptions<RebalancingConfigurationNode[]>;
};

export const useListAllRebalancingConfigurationNodesQuery = ({
  params,
  options,
}: QueryArgs) => {
  const currentOrganization = useCurrentOrganization();
  const organizationId = currentOrganization.id;

  return useQuery({
    queryKey: [LIST_REBALANCING_NODES_QUERY_KEY, params],
    queryFn: async () => {
      return fetchAllRebalancingNodes({
        organizationId,
        clusterId: params.clusterId,
      });
    },
    ...options,
    enabled: makeIsEnabled(
      !!organizationId,
      !!params.clusterId,
      options?.enabled
    ),
  });
};
